<script lang="ts">
  import { getCategories, upsertTransaction, bulkUpdateByDescription, undoBulkUpdate, checkRuleExists, createRule, applyRuleToExisting, splitTransaction, unsplitTransaction } from '../../lib/storage.js';
  import * as categoryOperations from '../../lib/storage/operations/categoryOperations';
  import { extractPattern, countSimilarTransactions, filterTransactionsByExclusion } from '../../lib/transactionUtils';
  import { sortTransactions } from '../../lib/analytics';
  import type { Transaction, TransactionEditValues, BulkEditValues, RuleSuggestion } from '../../types/transaction';

  // PWA migration: Stub for recurring interval updates (feature disabled)
  const updateRecurringInterval = async (pattern: string, avgInterval: number) => {
    console.log('[PWA] Recurring interval updates disabled:', { pattern, avgInterval });
  };
  import type { SortField, SortOrder } from '../../lib/types';

  import UndoNotification from './UndoNotification.svelte';
  import RuleSuggestionToast from './RuleSuggestionToast.svelte';
  import BulkActionsBar from './BulkActionsBar.svelte';
  import BulkEditPanel from './BulkEditPanel.svelte';
  import TransactionRow from './TransactionRow.svelte';
  import MonthNavigator from './MonthNavigator.svelte';
  import { ToastContainer } from '../../components/common';
  import ConfirmDialog from '../../components/common/ConfirmDialog.svelte';
  import { createConfirmDialog } from '../../lib/confirmDialog.svelte';
  import type { ToastItem } from '../../components/common';

  const confirmDialog = createConfirmDialog();

  let { transactions = [], showExcluded = false, onUpdate = () => {} }: {
    transactions: Transaction[];
    showExcluded?: boolean;
    onUpdate: () => void;
  } = $props();

  // State
  let categories = $state<string[]>([]);
  let editingId = $state<string | null>(null);
  let editValues = $state<TransactionEditValues>({} as TransactionEditValues);
  let originalTransaction = $state<Transaction | null>(null);
  let updateAllSimilar = $state(true);

  // Undo state
  let undoState = $state<any>(null);
  let showUndoNotification = $state(false);
  let undoTimeout = $state<number | undefined>(undefined);

  // Rule suggestion state
  let showRuleSuggestion = $state(false);
  let ruleSuggestionData = $state<RuleSuggestion | null>(null);
  let creatingRule = $state(false);

  // Bulk edit state
  let selectedIds = $state<Set<string>>(new Set());
  let bulkEditMode = $state(false);
  let bulkEditValues = $state<BulkEditValues>({
    category: '',
    person: '',
    displayName: ''
  });

  // Scroll state
  let scrollContainer: HTMLElement | null = $state(null);
  let showBackToTop = $state(false);
  let currentYear = $state<number | null>(null);
  let currentMonth = $state<number | null>(null);

  // Sort state
  let sortBy = $state<SortField>('date');
  let sortOrder = $state<SortOrder>('desc');

  // Derived state
  let filteredTransactions = $derived(
    sortTransactions(
      filterTransactionsByExclusion(transactions, showExcluded),
      sortBy,
      sortOrder
    )
  );

  let similarCount = $derived(
    editingId && originalTransaction
      ? countSimilarTransactions(transactions, originalTransaction.description)
      : 0
  );

  let isAllSelected = $derived(
    filteredTransactions.length > 0 && selectedIds.size === filteredTransactions.length
  );

  // Virtual scrolling setup - custom implementation
  // Only render visible rows based on scroll position
  let visibleStartIndex = $state(0);
  let visibleEndIndex = $state(50);

  // Calculate initial visible range when scroll container is ready
  $effect(() => {
    if (scrollContainer && filteredTransactions.length > 0) {
      const rowHeight = 57;
      const containerHeight = scrollContainer.clientHeight;
      const visibleRows = Math.ceil(containerHeight / rowHeight) + 20; // +20 for overscan
      visibleEndIndex = Math.min(visibleRows, filteredTransactions.length);
    }
  });

  let virtualItems = $derived.by(() => {
    const items = [];
    for (let i = visibleStartIndex; i < Math.min(visibleEndIndex, filteredTransactions.length); i++) {
      items.push({
        index: i,
        start: i * 57, // Row height
        size: 57
      });
    }
    return items;
  });

  let totalSize = $derived(filteredTransactions.length * 57);

  // Build toast array
  let toasts = $derived.by(() => {
    const items: ToastItem[] = [];

    if (showUndoNotification && undoState) {
      items.push({
        id: 'undo',
        component: UndoNotification,
        props: {
          count: undoState?.length || 0,
          onUndo: handleUndo,
          onDismiss: dismissUndoNotification
        },
        position: 'bottom-right'
      });
    }

    if (showRuleSuggestion && ruleSuggestionData) {
      items.push({
        id: 'rule-suggestion',
        component: RuleSuggestionToast,
        props: {
          suggestion: ruleSuggestionData,
          creating: creatingRule,
          onCreate: handleCreateRule,
          onCreateAndApply: handleCreateRuleAndApply,
          onDismiss: dismissRuleSuggestion
        },
        position: 'bottom-right'
      });
    }

    return items;
  });

  // Load categories on mount
  $effect(() => {
    getCategories().then(cats => {
      categories = cats;
    });
  });

  // Edit functions
  function startEdit(transaction: Transaction) {
    editingId = transaction.id;
    editValues = { ...transaction };
    originalTransaction = { ...transaction };
    updateAllSimilar = true;
  }

  function cancelEdit() {
    editingId = null;
    editValues = {} as TransactionEditValues;
    originalTransaction = null;
  }

  async function saveEdit() {
    if (!editingId || !editValues || !originalTransaction) return;

    const categoryChanged = editValues.category !== originalTransaction.category;
    const personChanged = editValues.person !== originalTransaction.person;
    const displayNameChanged = editValues.displayName !== originalTransaction.displayName;
    const isRecurringChanged = editValues.isRecurring !== originalTransaction.isRecurring;
    const recurringTypeChanged = editValues.recurringType !== originalTransaction.recurringType;
    const excludedChanged = editValues.excluded !== originalTransaction.excluded;

    if ((categoryChanged || personChanged || displayNameChanged || isRecurringChanged || recurringTypeChanged || excludedChanged) && updateAllSimilar) {
      // Bulk update all transactions with the same description
      const updates: any = {};
      if (categoryChanged) updates.category = editValues.category;
      if (personChanged) updates.person = editValues.person;
      if (displayNameChanged) updates.displayName = editValues.displayName;
      if (isRecurringChanged) updates.isRecurring = editValues.isRecurring;
      if (recurringTypeChanged) updates.recurringType = editValues.recurringType;
      if (excludedChanged) {
        updates.excluded = editValues.excluded;
        if (editValues.excluded) {
          updates.exclusionReason = 'user';
        } else {
          updates.exclusionReason = null;
        }
      }

      const result = await bulkUpdateByDescription(originalTransaction.description, updates);

      if (result.success) {
        undoState = result.previousStates;
        showUndoNotification = true;

        if (undoTimeout) clearTimeout(undoTimeout);
        undoTimeout = window.setTimeout(() => {
          showUndoNotification = false;
          undoState = null;
        }, 5000);

        // Check if a rule already exists for this pattern (only for fields that rules support)
        if (categoryChanged || personChanged || displayNameChanged) {
          const pattern = extractPattern(originalTransaction.description);
          const ruleCheck = await checkRuleExists(pattern);

          if (!ruleCheck.exists) {
            ruleSuggestionData = {
              pattern,
              category: editValues.category,
              person: personChanged ? editValues.person : null,
              description: originalTransaction.description,
              normalizedName: editValues.displayName || null
            };
            showRuleSuggestion = true;
          }
        }

        onUpdate();
      }
    } else {
      // Single transaction update
      const updated: Transaction = {
        ...editValues,
        categorySource: 'user'
      };

      if (updated.excluded && !originalTransaction.excluded) {
        updated.exclusionReason = 'user';
        updated.excludedBy = 'user';
        updated.excludedAt = new Date().toISOString();
      } else if (!updated.excluded && originalTransaction.excluded) {
        updated.exclusionReason = null;
        updated.excludedBy = null;
        updated.excludedAt = null;
      }

      await upsertTransaction(updated);

      // Handle recurring interval update if specified
      if (updated.isRecurring && editValues.recurringInterval) {
        try {
          const pattern = extractPattern(originalTransaction.description);
          await updateRecurringInterval(pattern, editValues.recurringInterval);
        } catch (err) {
          console.error('Error updating recurring interval:', err);
        }
      }

      onUpdate();
    }

    editingId = null;
    editValues = {} as TransactionEditValues;
    originalTransaction = null;
  }

  // Undo functions
  async function handleUndo() {
    if (undoState) {
      const result = await undoBulkUpdate(undoState);
      if (result.success) {
        showUndoNotification = false;
        undoState = null;
        if (undoTimeout) clearTimeout(undoTimeout);
        onUpdate();
      }
    }
  }

  function dismissUndoNotification() {
    showUndoNotification = false;
    undoState = null;
    if (undoTimeout) clearTimeout(undoTimeout);
  }

  // Rule suggestion functions
  async function handleCreateRule() {
    if (!ruleSuggestionData) return;

    creatingRule = true;
    const result = await createRule({
      pattern: ruleSuggestionData.pattern,
      category: ruleSuggestionData.category,
      person: ruleSuggestionData.person,
      normalizedName: ruleSuggestionData.normalizedName
    });

    if (result.success) {
      showRuleSuggestion = false;
      ruleSuggestionData = null;
      onUpdate();
    }
    creatingRule = false;
  }

  async function handleCreateRuleAndApply() {
    if (!ruleSuggestionData) return;

    creatingRule = true;
    const result = await createRule({
      pattern: ruleSuggestionData.pattern,
      category: ruleSuggestionData.category,
      person: ruleSuggestionData.person,
      normalizedName: ruleSuggestionData.normalizedName
    });

    if (result.success && result.rule) {
      await applyRuleToExisting(result.rule.id, true);
      showRuleSuggestion = false;
      ruleSuggestionData = null;
      onUpdate();
    }
    creatingRule = false;
  }

  function dismissRuleSuggestion() {
    showRuleSuggestion = false;
    ruleSuggestionData = null;
  }

  // Bulk selection functions
  function toggleSelection(id: string) {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    selectedIds = newSet;
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredTransactions.length) {
      selectedIds = new Set();
    } else {
      selectedIds = new Set(filteredTransactions.map(t => t.id));
    }
  }

  function startBulkEdit() {
    if (selectedIds.size === 0) return;
    bulkEditMode = true;
    const firstSelected = transactions.find(t => selectedIds.has(t.id));
    if (firstSelected) {
      bulkEditValues = {
        category: firstSelected.category,
        person: firstSelected.person,
        displayName: '' // Leave empty by default - user can optionally set
      };
    }
  }

  function cancelBulkEdit() {
    bulkEditMode = false;
    bulkEditValues = {
      category: '',
      person: '',
      displayName: ''
    };
  }

  async function saveBulkEdit() {
    if (selectedIds.size === 0) return;

    const previousStates = transactions
      .filter(t => selectedIds.has(t.id))
      .map(t => ({ ...t }));

    for (const transaction of transactions) {
      if (selectedIds.has(transaction.id)) {
        const updated: Transaction = {
          ...transaction,
          category: bulkEditValues.category,
          person: bulkEditValues.person,
          categorySource: 'user'
        };

        // Apply display name if provided (not empty or whitespace)
        if (bulkEditValues.displayName && bulkEditValues.displayName.trim()) {
          updated.displayName = bulkEditValues.displayName.trim();
        }

        await upsertTransaction(updated);
      }
    }

    undoState = previousStates;
    showUndoNotification = true;

    if (undoTimeout) clearTimeout(undoTimeout);
    undoTimeout = window.setTimeout(() => {
      showUndoNotification = false;
      undoState = null;
    }, 5000);

    selectedIds = new Set();
    bulkEditMode = false;
    bulkEditValues = {
      category: '',
      person: '',
      displayName: ''
    };

    onUpdate();
  }

  // Create category function
  async function handleCreateCategory(categoryName: string) {
    const result = await categoryOperations.add(categoryName);
    if (result.success && result.categories) {
      categories = result.categories;
      // The SearchableSelect will handle selecting the new category
    } else {
      console.error('Failed to create category:', result.error);
      throw new Error(result.error || 'Failed to create category');
    }
  }

  // Split transaction function
  async function handleSplit(transaction: Transaction, splits: any[]) {
    const result = await splitTransaction(transaction.id, splits);

    if (result.success) {
      // Clear edit state if we were editing the split transaction
      if (editingId === transaction.id) {
        cancelEdit();
      }
      onUpdate();
    } else {
      alert(`Failed to split transaction: ${result.error}`);
    }
  }

  // Unsplit transaction function
  async function handleUnsplit(transaction: Transaction) {
    const confirmed = await confirmDialog.confirm({
      title: 'Unsplit Transaction',
      message: 'Are you sure you want to unsplit this transaction? This will remove the split transactions and restore the original.',
      confirmText: 'Unsplit',
      variant: 'warning'
    });

    if (!confirmed) return;

    const result = await unsplitTransaction(transaction.id);

    if (result.success) {
      onUpdate();
    } else {
      alert(`Failed to unsplit transaction: ${result.error}`);
    }
  }

  // Scroll functions
  function handleScroll(e: Event) {
    const target = e.target as HTMLElement;
    showBackToTop = target.scrollTop > 500;

    const scrollTop = target.scrollTop;
    const rowHeight = 57; // Approximate row height
    const containerHeight = target.clientHeight;

    // Calculate visible range for virtual scrolling
    const overscan = 10; // Extra rows to render above/below viewport
    visibleStartIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    visibleEndIndex = Math.min(
      filteredTransactions.length,
      Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan
    );

    // Find the first visible transaction to update current month/year indicator
    const visibleIndex = Math.floor(scrollTop / rowHeight);
    if (visibleIndex >= 0 && visibleIndex < filteredTransactions.length) {
      const visibleTransaction = filteredTransactions[visibleIndex];
      const date = new Date(visibleTransaction.date);
      currentYear = date.getFullYear();
      currentMonth = date.getMonth();
    }
  }

  function scrollToTop() {
    scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function scrollToMonth(year: number, month: number) {
    // Find first transaction matching the year/month
    const index = filteredTransactions.findIndex(t => {
      const date = new Date(t.date);
      return date.getFullYear() === year && date.getMonth() === month;
    });

    if (index !== -1 && scrollContainer) {
      // Calculate approximate position (57px per row + 50px for header)
      const scrollTop = index * 57 + 50;
      scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  }

  // Sorting functions
  function handleSort(field: SortField) {
    if (sortBy === field) {
      // Toggle sort order if clicking the same column
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column: default to descending for date/amount, ascending for others
      sortBy = field;
      sortOrder = (field === 'date' || field === 'amount') ? 'desc' : 'asc';
    }
  }
</script>

<div class="transaction-table-wrapper">
  {#if transactions.length === 0}
    <div class="transaction-table">
      <div class="empty-state">
        <p>No transactions to display</p>
      </div>
    </div>
  {:else}
    <div class="content-with-nav">
      <div class="transaction-table">
        {#if selectedIds.size > 0 && !bulkEditMode}
          <BulkActionsBar
            selectedCount={selectedIds.size}
            onEdit={startBulkEdit}
            onClearSelection={() => selectedIds = new Set()}
          />
        {/if}

        {#if bulkEditMode}
          <BulkEditPanel
            selectedCount={selectedIds.size}
            {categories}
            bind:values={bulkEditValues}
            onSave={saveBulkEdit}
            onCancel={cancelBulkEdit}
            onCreateCategory={handleCreateCategory}
          />
        {/if}

        <div
          class="table-scroll-container"
          bind:this={scrollContainer}
          onscroll={handleScroll}
        >
          <table>
            <thead>
              <tr>
                <th class="checkbox-col">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onchange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th class="sortable" onclick={() => handleSort('date')}>
                  <span class="th-content">
                    Date
                    {#if sortBy === 'date'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th class="sortable" onclick={() => handleSort('description')}>
                  <span class="th-content">
                    Description
                    {#if sortBy === 'description'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th class="sortable" onclick={() => handleSort('category')}>
                  <span class="th-content">
                    Category
                    {#if sortBy === 'category'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th class="sortable" onclick={() => handleSort('institution')}>
                  <span class="th-content">
                    Source
                    {#if sortBy === 'institution'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th class="sortable" onclick={() => handleSort('amount')}>
                  <span class="th-content">
                    Amount
                    {#if sortBy === 'amount'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th class="sortable" onclick={() => handleSort('person')}>
                  <span class="th-content">
                    Person
                    {#if sortBy === 'person'}
                      <span class="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    {/if}
                  </span>
                </th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              <!-- Spacer before visible items to maintain scroll position -->
              {#if visibleStartIndex > 0}
                <tr style="height: {visibleStartIndex * 57}px;"></tr>
              {/if}

              <!-- Render only visible items (no absolute positioning needed) -->
              {#each virtualItems as virtualRow (filteredTransactions[virtualRow.index]?.id || `row-${virtualRow.index}`)}
                {@const transaction = filteredTransactions[virtualRow.index]}
                {#if transaction}
                  <TransactionRow
                    {transaction}
                    {categories}
                    isEditing={editingId === transaction.id}
                    isSelected={selectedIds.has(transaction.id)}
                    bind:editValues
                    bind:updateAllSimilar
                    similarCount={similarCount}
                    onEdit={() => startEdit(transaction)}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    onToggleSelection={() => toggleSelection(transaction.id)}
                    onSplit={(splits) => handleSplit(transaction, splits)}
                    onUnsplit={() => handleUnsplit(transaction)}
                    onCreateCategory={handleCreateCategory}
                  />
                {/if}
              {/each}

              <!-- Spacer after visible items to maintain total height -->
              {#if visibleEndIndex < filteredTransactions.length}
                <tr style="height: {(filteredTransactions.length - visibleEndIndex) * 57}px;"></tr>
              {/if}
            </tbody>
          </table>
        </div>

        {#if showBackToTop}
          <button class="back-to-top" onclick={scrollToTop} title="Back to top">
            ↑
          </button>
        {/if}
      </div>

      {#if sortBy === 'date'}
        <aside class="nav-sidebar">
          <MonthNavigator
            {transactions}
            onMonthSelect={scrollToMonth}
            {currentYear}
            {currentMonth}
          />
        </aside>
      {/if}
    </div>
  {/if}
</div>

<ToastContainer {toasts} />
<ConfirmDialog {...confirmDialog.props} />

<style lang="scss">
  .transaction-table-wrapper {
    position: relative;
    overflow: visible; /* Allow tooltips to overflow */
  }

  .content-with-nav {
    display: flex;
    gap: var(--spacing-lg);
    align-items: flex-start;
    overflow: visible; /* Allow tooltips to overflow */
  }

  .transaction-table {
    flex: 1;
    background: var(--color-background, white);
    border-radius: var(--radius-lg, 8px);
    box-shadow: var(--shadow-md, 0 2px 4px rgba(0, 0, 0, 0.1));
    overflow: hidden;
    position: relative;
    min-width: 0;
  }

  .nav-sidebar {
    flex-shrink: 0;
    overflow: visible; /* Allow tooltips to overflow */
  }

  .empty-state {
    padding: 40px;
    text-align: center;
    color: var(--color-text-tertiary);
  }

  .table-scroll-container {
    max-height: calc(100vh - 250px);
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
  }

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  thead {
    background: var(--color-background-tertiary);
    border-bottom: 2px solid var(--color-border);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th {
    padding: var(--spacing-md);
    text-align: left;
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  th.sortable {
    cursor: pointer;
    user-select: none;
    transition: all var(--transition-fast);

    &:hover {
      background: var(--color-background-secondary);
      color: var(--color-primary);
    }

    &:active {
      background: var(--color-background-tertiary);
    }
  }

  .th-content {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .sort-indicator {
    font-size: var(--font-size-base);
    color: var(--color-primary);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-none);
  }

  .checkbox-col {
    width: 40px;
    text-align: center;
    padding: var(--spacing-sm) !important;

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
    }
  }

  /* Column widths - Fixed to prevent jumping */
  th:nth-child(1), td:nth-child(1) { /* Checkbox */
    width: 40px;
    min-width: 40px;
    max-width: 40px;
  }

  th:nth-child(2), td:nth-child(2) { /* Date */
    width: 110px;
    min-width: 110px;
    max-width: 110px;
  }

  th:nth-child(3), td:nth-child(3) { /* Description */
    width: 300px;
    min-width: 300px;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th:nth-child(4), td:nth-child(4) { /* Category */
    width: 150px;
    min-width: 150px;
    max-width: 150px;
  }

  th:nth-child(5), td:nth-child(5) { /* Source */
    width: 150px;
    min-width: 150px;
    max-width: 150px;
  }

  th:nth-child(6), td:nth-child(6) { /* Amount */
    width: 110px;
    min-width: 110px;
    max-width: 110px;
  }

  th:nth-child(7), td:nth-child(7) { /* Person */
    width: 120px;
    min-width: 120px;
    max-width: 120px;
  }

  th:nth-child(8), td:nth-child(8) { /* Edit */
    width: 90px;
    min-width: 90px;
    max-width: 90px;
  }

  .back-to-top {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    background: var(--color-primary-bright);
    color: white;
    border: none;
    box-shadow: var(--shadow-glow-md);
    cursor: pointer;
    font-size: var(--font-size-xl);
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-slow);
    z-index: var(--z-modal);

    &:hover {
      background: var(--color-primary);
      transform: translateY(-2px);
      box-shadow: var(--shadow-glow-lg);
    }

    &:active {
      transform: translateY(0);
      box-shadow: var(--shadow-glow-sm);
    }
  }

  /* Tablet and below - hide month navigator sidebar */
  @media (max-width: 1024px) {
    .nav-sidebar {
      display: none;
    }
  }

  /* Tablet portrait and below - hide less important columns */
  @media (max-width: 768px) {
    th:nth-child(7), /* Person */
    th:nth-child(5) { /* Source */
      display: none;
    }

    .back-to-top {
      bottom: 80px; /* Account for bottom nav */
      right: 16px;
      width: 40px;
      height: 40px;
      font-size: var(--font-size-lg);
    }

    .table-scroll-container {
      overflow-y: auto;
      overflow-x: auto;

      max-height: calc(100vh - 300px);
    }
  }

  /* Large phones and below - simplify further */
  @media (max-width: 640px) {
    .table-scroll-container {
      overflow-x: auto;
      overflow-y: auto;

      -webkit-overflow-scrolling: touch;
    }

    th, td {
      font-size: var(--font-size-sm);
      padding: var(--spacing-sm) 6px;
    }

    .checkbox-col {
      width: 32px;

      input[type="checkbox"] {
        width: 16px;
        height: 16px;
      }
    }
  }
</style>
